import {
    addNavMenuSection,
    Permission,
    registerFormInputComponent,
    addActionBarDropdownMenuItem,
    registerBulkAction,
    ModalService,
    DataService,
    NotificationService
} from '@vendure/admin-ui/core';
import { allow, isOnboardingCompleted, onboardingGated } from '../common/ui/utils';
import { APP_INITIALIZER } from '@angular/core';
import { TawkChatService } from './services/tawk-chat.service';
import { ExternalServicesInputComponent } from './components/seller-external-services-settings-form';
import {
    onOrderFulfillmentImport,
    exportUnshippedOrderFulfillments,
    exportSelectedOrders,
    markOrdersAsDelivered,
} from './actions/order-page-actions';
import { createExportDialog } from './actions/export-select-format-dialog';
import { CustomPermissions } from '../common/ui/ui-types';


export default [
    addNavMenuSection({
        id: 'setup',
        label: 'Setup',
        icon: 'cog',
        items: [
            {
                id: 'onboarding-dashboard',
                label: 'Onboarding',
                routerLink: ['/'],
                icon: 'dashboard',
                requiresPermission: () => !isOnboardingCompleted(),
            },
        ],
        requiresPermission: () => !isOnboardingCompleted(),
    }, 'catalog'),
    addNavMenuSection({
        id: 'catalog',
        label: 'nav.catalog',
        requiresPermission: onboardingGated(Permission.ReadCatalog, Permission.ReadProduct, Permission.ReadFacet, Permission.ReadCollection, Permission.ReadAsset),
        items: [
            { requiresPermission: allow(Permission.ReadCatalog, Permission.ReadProduct), id: 'products', label: 'nav.products', icon: 'library', routerLink: ['/catalog', 'products'] },
            { requiresPermission: allow(Permission.ReadCatalog, Permission.ReadFacet), id: 'facets', label: 'nav.facets', icon: 'tag', routerLink: ['/catalog', 'facets'] },
            { requiresPermission: allow(Permission.ReadCatalog, Permission.ReadCollection), id: 'collections', label: 'nav.collections', icon: 'folder-open', routerLink: ['/catalog', 'collections'] },
            { requiresPermission: allow(Permission.ReadCatalog, Permission.ReadAsset), id: 'assets', label: 'nav.assets', icon: 'image-gallery', routerLink: ['/catalog', 'assets'] },
        ],
    }),
    addNavMenuSection({
        id: 'sales',
        label: 'nav.sales',
        requiresPermission: onboardingGated(Permission.ReadOrder),
        items: [
            { requiresPermission: allow(Permission.ReadOrder), id: 'orders', label: 'nav.orders', routerLink: ['/orders'], icon: 'shopping-cart' },
        ],
    }),
    addNavMenuSection({
        id: 'marketing',
        label: 'nav.marketing',
        requiresPermission: onboardingGated(Permission.ReadPromotion),
        items: [
            { requiresPermission: allow(Permission.ReadPromotion), id: 'promotions', label: 'nav.promotions', routerLink: ['/marketing', 'promotions'], icon: 'asterisk' },
        ],
    }),
    addNavMenuSection({
        id: 'customers',
        label: 'Customers',
        requiresPermission: onboardingGated(Permission.ReadCustomer, Permission.ReadCustomerGroup),
        items: [
            {
                requiresPermission: allow(Permission.ReadCustomer),
                id: 'customers',
                label: 'Customers',
                routerLink: ['/customer', 'customers'],
                icon: 'user',
            },
            {
                requiresPermission: allow(Permission.SuperAdmin),
                id: 'customer-groups',
                label: 'Customer Groups',
                routerLink: ['/customer', 'groups'],
                icon: 'users',
            },
        ],
    }, 'sales'),
    addNavMenuSection({
        id: 'store-settings',
        label: 'Store Settings',
        icon: 'settings',
        requiresPermission: onboardingGated(CustomPermissions.ReadSite),
        items: [
            {
                requiresPermission: allow(CustomPermissions.ReadSite),
                id: 'store-profile',
                label: 'General',
                routerLink: ['/store-settings', 'general'],
                icon: 'store',
            },
            {
                requiresPermission: allow(Permission.ReadPaymentMethod),
                id: 'store-payment',
                label: 'Get Paid',
                routerLink: ['/store-settings', 'store-payment'],
                icon: 'credit-card',
            },
            {
                requiresPermission: allow(Permission.ReadStockLocation),
                id: 'stock-locations',
                label: 'Stock Locations',
                icon: 'map-marker',
                routerLink: ['/settings', 'stock-locations'],
            },
            {
                requiresPermission: allow(Permission.ReadShippingMethod),
                id: 'shipping-methods',
                label: 'Shipping Methods',
                routerLink: ['/settings', 'shipping-methods'],
                icon: 'truck',
            },
            {
                requiresPermission: allow(Permission.UpdateCatalog),
                id: 'parcel-profiles',
                label: 'Parcel Profiles',
                routerLink: ['/store-settings', 'parcel-profiles'],
                icon: 'settings',
            },
            {
                id: 'store-pages',
                label: 'Site Pages',
                routerLink: ['/store-settings', 'store-pages'],
                icon: 'file',
                requiresPermission: allow(Permission.SuperAdmin),
            },
            {
                requiresPermission: allow(Permission.SuperAdmin),
                id: 'store-integrations',
                label: 'Integrations',
                routerLink: ['/store-settings', 'integrations'],
                icon: 'plugin',
            },
            {
                requiresPermission: allow(Permission.SuperAdmin),
                id: 'store-design',
                label: 'Design',
                onClick: (event: MouseEvent) => {
                    event.preventDefault();
                    window.location.href = '/design/';
                },
                routerLink: [],
                icon: 'paint-roller',
            },
        ],
    }, 'settings'),
    // addNavMenuSection({
    //     id: 'seller-settings',
    //     label: 'Seller Settings',
    //     icon: 'settings',
    //     requiresPermission: allow(CustomPermissions.CreateCustomDomain),
    //     items: [ ],
    // }, 'settings'),

    registerFormInputComponent('external-services-form', ExternalServicesInputComponent),

    //////////////////////////////////////////////////////////////
    // Order List page - DropdownMenu & Actions
    //////////////////////////////////////////////////////////////

    addActionBarDropdownMenuItem({
        id: 'ch-order-export-all-unshipped',
        label: 'Export All Unshipped Orders',
        locationId: 'order-list',
        icon: 'export',
        requiresPermission: 'ReadOrder',
        hasDivider: true,
        onClick: async (event, context) => {
            console.log('Export All Unshipped Order');
            await exportSelectedOrders('export-all', [], context.dataService, context.notificationService);
        },
    }),

    addActionBarDropdownMenuItem({
        id: 'ch-order-export-all-unshipped-fulfillments',
        label: 'Export All Unshipped Order Fulfillments',
        locationId: 'order-list',
        icon: 'export',
        requiresPermission: 'ReadOrder',
        hasDivider: false,
        onClick: async (event, context) => {
            console.log('Export All Unshipped Order Fulfillments');
            const modalService = context.injector.get(ModalService);
            await createExportDialog(modalService, 'Export All Unshipped Order Fulfillments', async (format) => {
                await exportUnshippedOrderFulfillments('export-all', [], format, context.dataService, context.notificationService);
            });
        },
    }),

    addActionBarDropdownMenuItem({
        id: 'ch-order-import-fulfillments',
        label: 'Import Order Fulfillments Dealomega Format',
        locationId: 'order-list',
        icon: 'import',
        requiresPermission: 'UpdateOrder',
        hasDivider: true,
        onClick: async (event, context) => {
            console.log('Import Order Fulfillments Dealomega Format');
            await onOrderFulfillmentImport('order-import-format-dealomega', context.dataService, context.notificationService);
        },
    }),

    addActionBarDropdownMenuItem({
        id: 'ch-order-import-fulfillments-universal',
        label: 'Import Order Fulfillments Universal Format',
        locationId: 'order-list',
        icon: 'import',
        requiresPermission: 'UpdateOrder',
        hasDivider: true,
        onClick: async (event, context) => {
            console.log('Import Order Fulfillments Universal Format');
            await onOrderFulfillmentImport('order-import-format-dealomega-universal', context.dataService, context.notificationService);
        },
    }),

    registerBulkAction({
        location: 'order-list',
        label: 'Export Selected Orders',
        icon: 'export',
        requiresPermission: 'ReadOrder',
        onClick: async (context) => {
            const orderIds = context.selection.map(item => item.id);
            const dataService = context.injector.get(DataService);
            const notificationService = context.injector.get(NotificationService);
            await exportSelectedOrders('export-bulk', orderIds, dataService, notificationService);
        },
    }),

    registerBulkAction({
        location: 'order-list',
        label: 'Export Selected Order Fulfillments',
        icon: 'export',
        requiresPermission: 'ReadOrder',
        onClick: async (context) => {
            const orderIds = context.selection.map(item => item.id);
            const dataService = context.injector.get(DataService);
            const notificationService = context.injector.get(NotificationService);
            const modalService = context.injector.get(ModalService);
            await createExportDialog(modalService, 'Export Selected Order Fulfillments', async (format) => {
                await exportUnshippedOrderFulfillments('export-bulk', orderIds, format, dataService, notificationService);
            });
        },
    }),

    registerBulkAction({
        location: 'order-list',
        label: 'Mark as Delivered',
        icon: 'check-circle',
        requiresPermission: 'UpdateOrder',
        onClick: async (context) => {
            const orderIds = context.selection.map(item => item.id);
            const dataService = context.injector.get(DataService);
            const notificationService = context.injector.get(NotificationService);
            const anyUpdated = await markOrdersAsDelivered(orderIds, dataService, notificationService);
            if (anyUpdated) {
                context.hostComponent.refresh();
                context.clearSelection();
            }
        },
    }),
    {
        provide: APP_INITIALIZER,
        useFactory: (tawkChatService: TawkChatService) => () => tawkChatService.init(),
        deps: [TawkChatService],
        multi: true,
    },
];
